import { useCallback, useEffect, useMemo } from 'react';

import emptyPng from '@assets/empty.png';
import { Button } from '@components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import { useAuth } from '@contexts/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import DrugService from '@services/drugs/DrugService';
import { type IGetAllDrugsOutputData } from '@services/drugs/dtos/getAllDrugs/OutputData';
import { type IGetTypesDrugOutputData } from '@services/drugs/dtos/getTypesDrug/OutputData';
import { type IAddDrugToFavoritesInputData } from '@services/favorites/dtos/addDrugToFavorites/InputData';
import { type IRemoveDrugFromFavoritesInputData } from '@services/favorites/dtos/removeDrugFromFavorites/InputData';
import FavoriteService from '@services/favorites/FavoriteService';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Check, ChevronsUpDown, Heart, LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

const formSchema = z.object({
  type: z.string(),
  activeIngredient: z.string(),
});

function DrugsList() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    type: '0',
  });

  const {
    isPending: isPendingAdd,
    mutateAsync: mutateAsyncAdd,
    variables: variablesAdd,
  } = useMutation<void, unknown, IAddDrugToFavoritesInputData>({
    mutationFn: FavoriteService.addDrug,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drugs'] }),
  });

  const {
    isPending: isPendingRemove,
    mutateAsync: mutateAsyncRemove,
    variables: variablesRemove,
  } = useMutation<void, unknown, IRemoveDrugFromFavoritesInputData>({
    mutationFn: FavoriteService.removeDrug,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['drugs'] }),
  });

  const handleFavorite = useCallback(
    async (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      id: number,
      isFavorite: boolean,
    ) => {
      event.stopPropagation();
      event.preventDefault();
      if (isFavorite) {
        await mutateAsyncRemove({ id });
      } else {
        await mutateAsyncAdd({
          id,
        });
      }
    },
    [mutateAsyncAdd, mutateAsyncRemove],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      type: '0',
      activeIngredient: searchParams.get('activeIngredient') || '',
    },
  });

  const { data: drugs } = useSuspenseQuery<IGetAllDrugsOutputData>({
    queryFn: async () => {
      const response = await DrugService.getAll({
        userId: user?.id,
        typeDrugId:
          searchParams.get('type') === '0'
            ? undefined
            : Number(searchParams.get('type')),
      });
      return response;
    },
    queryKey: ['drugs', user?.id, searchParams.get('type')],
    refetchOnWindowFocus: false,
  });

  const { data: drugTypes } = useQuery<IGetTypesDrugOutputData>({
    queryFn: async () => {
      const response = await DrugService.getTypes();
      return response;
    },
    queryKey: ['drugTypes'],
    refetchOnWindowFocus: false,
  });

  const drugsFiltered = useMemo(() => {
    if (searchParams.get('activeIngredient')) {
      return drugs.list.filter((item) =>
        item.name
          .toLowerCase()
          .includes(searchParams.get('activeIngredient')!.toLowerCase()),
      );
    }
    return drugs?.list;
  }, [drugs?.list, searchParams]);

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    navigate(`${location.pathname}?${searchParams}`, {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'type') {
        const valuesFiltered = Object.fromEntries(
          Object.entries(value).filter(([_, value]) => value !== ''),
        );
        setSearchParams(
          () => ({
            ...valuesFiltered,
          }),
          { replace: true },
        );
      }
      if (name === 'activeIngredient') {
        const valuesFiltered = Object.fromEntries(
          Object.entries(value).filter(([_, value]) => value !== ''),
        );
        setSearchParams(
          () => ({
            ...valuesFiltered,
          }),
          { replace: true },
        );
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  return (
    <div>
      <Form {...form}>
        <form
          className="flex flex-col justify-end gap-2 sm:flex-row"
          onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="activeIngredient"
            render={({ field }) => (
              <FormItem className="flex w-full flex-col sm:max-w-72">
                <FormLabel className="text-tertiary">Princípio ativo</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="flex w-full flex-col sm:max-w-72">
                <FormLabel className="text-tertiary">Vias de uso</FormLabel>
                <Popover>
                  <PopoverTrigger asChild className="w-full">
                    <FormControl>
                      <Button
                        className={cn(
                          'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                          !field.value && 'text-muted-foreground',
                        )}
                        role="combobox"
                        variant="outline">
                        {field.value
                          ? [{ id: 0, name: 'Todas as vias de uso' }]
                              .concat(drugTypes?.list || [])
                              .find(
                                (option) =>
                                  option.id.toString() === field.value,
                              )?.name
                          : ''}
                        <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                    <Command>
                      <CommandInput
                        className="placeholder:text-foreground/70 h-9"
                        placeholder="Buscar..."
                      />
                      <CommandList>
                        <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                        <CommandGroup>
                          {[{ id: 0, name: 'Todas as vias de uso' }]
                            .concat(drugTypes?.list || [])
                            .map((option) => (
                              <CommandItem
                                className="data-[selected=true]:bg-tertiary"
                                key={option.id}
                                onSelect={() => {
                                  form.setValue(`type`, option.id.toString(), {
                                    shouldValidate: true,
                                  });
                                }}
                                value={option.name}>
                                <PopoverClose className="flex w-full flex-row items-center">
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      option.id.toString() === field.value
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {option.name}
                                </PopoverClose>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </FormItem>
            )}
          />
        </form>
      </Form>

      {drugsFiltered?.length ? (
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          {drugsFiltered.map((item) => (
            <Link
              className={cn(
                'border-primary bg-background text-primary shadow-foreground/50 hover:shadow-foreground/60 flex flex-row items-center justify-between gap-2 rounded-lg border-2 p-3 text-base font-bold shadow-md transition-all hover:scale-[1.02] lg:p-4 lg:text-lg',
              )}
              key={item.id}
              to={`/medicamentos/${item.id}`}>
              {item.name}

              {(isPendingAdd && variablesAdd.id === item.id) ||
              (isPendingRemove && variablesRemove.id === item.id) ? (
                <LoaderCircle className="text-tertiary size-8 animate-spin" />
              ) : (
                <button
                  className={cn(
                    '[&>svg]:text-tertiary hover:[&>svg]:fill-tertiary/50 cursor-pointer [&>svg]:size-8',
                    item.isFavorite &&
                      '[&>svg]:fill-tertiary hover:[&>svg]:fill-background',
                  )}
                  onClick={(e) => {
                    handleFavorite(e, item.id, item.isFavorite);
                  }}>
                  <Heart strokeWidth={1} />
                </button>
              )}
            </Link>
          ))}
        </div>
      ) : (
        <div className="mt-36 flex flex-col justify-center">
          <img
            alt="Imagem de listagem vazia"
            className="mx-auto max-w-80"
            src={emptyPng}
          />
          <p className="mt-4 text-center text-lg font-bold">
            Não há registros!
          </p>
          <p className="mt-4 text-center text-lg">
            Não encontramos medicamentos
          </p>
        </div>
      )}
    </div>
  );
}

export default DrugsList;
